import type { ReactNode } from 'react';

import { createKeyboardClickHandler } from '@/utils/accessibility';

import { Box } from '@/components/Box';
import type { IconProps } from '@/components/Icon';
import { Icon } from '@/components/Icon';
import { LegacyButton } from '@/components/LegacyButton';

import * as S from './SecondaryCTA.styles';

export interface SecondaryCTAProps {
  items: {
    /**
     * BEC-24926: the PDP shows Call to buy here on desktop and 4Gift here on
     * mobile, so an item can opt out of one breakpoint. Hidden with
     * `display: none`, which also takes it out of the accessibility tree and
     * the tab order - only the visible one is ever announced.
     */
    hiddenOn?: 'desktop' | 'mobile';
    icon?: IconProps['name'];
    /**
     * Stable identity for the row. The list is assembled conditionally, so the
     * same entry lands on a different index depending on which flags are on -
     * keying by index would make React reuse the wrong row's DOM.
     */
    id: string;
    message: ReactNode;
    onClick?: () => void;
  }[];
}

export const SecondaryCTA = ({ items }: SecondaryCTAProps) => {
  return (
    <Box data-testid='SecondaryCTA'>
      {items.map(({ hiddenOn, icon, id, message, onClick }, i) => (
        <S.Row key={id} $hiddenOn={hiddenOn}>
          <LegacyButton
            data-testid={`SecondaryCTAItem-${i}`}
            label={message}
            leftContent={icon ? <Icon name={icon} size='s' /> : undefined}
            mt={2}
            onClick={onClick}
            onKeyDown={createKeyboardClickHandler(() => onClick?.())}
            role='button'
            size='l'
            tabIndex={0}
            variant='link'
            width='auto'
          />
        </S.Row>
      ))}
    </Box>
  );
};
